import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import EntSkeleton from '../../components/enterprise/EntSkeleton';
import '../../styles/EnterpriseDashboard.css';

const INVITE_RETURN_KEY = 'veloxEnterpriseInviteReturn';

export default function AcceptEnterpriseInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid invite link');
      setLoading(false);
      return;
    }
    sessionStorage.setItem(INVITE_RETURN_KEY, `/enterprise-invite/${token}`);
    let cancelled = false;
    api.get(`/api/enterprise/invite/${token}`)
      .then((res) => {
        if (!cancelled) setInvite(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Invite not found or expired');
        if (err.response?.data?.status) setInvite({ status: err.response.data.status });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    const t = localStorage.getItem('token');
    if (!t || !invite) {
      setAuthChecked(true);
      return;
    }
    let cancelled = false;
    api.get('/api/user/account')
      .then((res) => {
        if (!cancelled) setCurrentUserEmail((res.data?.email || '').toLowerCase());
      })
      .catch(() => { if (!cancelled) setCurrentUserEmail(null); })
      .finally(() => { if (!cancelled) setAuthChecked(true); });
    return () => { cancelled = true; };
  }, [invite]);

  const handleAccept = () => {
    setAccepting(true);
    api.post('/api/enterprise/accept-invite', { token })
      .then((res) => {
        sessionStorage.removeItem(INVITE_RETURN_KEY);
        showSuccess(res.data.message || 'You have joined the enterprise');
        navigate('/enterprise-app', { replace: true });
      })
      .catch((err) => {
        const msg = err.response?.data?.error || 'Failed to accept invite';
        showError(msg);
        if (err.response?.status === 401) {
          navigate('/login', { state: { returnUrl: `/enterprise-invite/${token}` } });
        }
      })
      .finally(() => setAccepting(false));
  };

  const inviteEmail = (invite?.email || '').toLowerCase();
  const emailMatches = currentUserEmail && inviteEmail && currentUserEmail === inviteEmail;
  const wrongAccount = authChecked && currentUserEmail && inviteEmail && !emailMatches;
  const readyToAccept = authChecked && emailMatches && invite?.status === 'pending';

  if (loading) {
    return (
      <div className="ent-app" style={{ justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <div className="ent-content" style={{ maxWidth: 480, width: '100%' }}>
          <EntSkeleton variant="block" />
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="ent-app" style={{ justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <div className="ent-content" style={{ maxWidth: 480, width: '100%' }}>
          <div className="ent-flash ent-flash-error" role="alert">{error}</div>
          <button type="button" className="ent-btn-primary" onClick={() => navigate('/login')}>
            Go to login
          </button>
        </div>
      </div>
    );
  }

  if (invite?.status === 'expired') {
    return (
      <div className="ent-app" style={{ justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <div className="ent-content" style={{ maxWidth: 480 }}>
          <h1 className="ent-page-title">Invite expired</h1>
          <p className="ent-page-subtitle">This invite has expired. Ask your admin to send a new one.</p>
          <button type="button" className="ent-btn-primary" onClick={() => navigate('/login')}>
            Go to login
          </button>
        </div>
      </div>
    );
  }

  if (invite?.status === 'accepted') {
    return (
      <div className="ent-app" style={{ justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <div className="ent-content" style={{ maxWidth: 480 }}>
          <h1 className="ent-page-title">Already accepted</h1>
          <p className="ent-page-subtitle">You have already accepted this invite and joined the enterprise.</p>
          <button type="button" className="ent-btn-primary" onClick={() => navigate('/enterprise-app')}>
            Open dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ent-app" style={{ justifyContent: 'center', alignItems: 'center', padding: 40 }}>
      <div className="ent-content" style={{ maxWidth: 480 }}>
        <h1 className="ent-page-title">Enterprise invite</h1>
        <p className="ent-page-subtitle">
          {invite?.inviterName} has invited you to join <strong>{invite?.enterpriseName}</strong> on VELOX Network.
        </p>
        <p className="ent-page-subtitle">
          This invite was sent to <strong>{invite?.email}</strong>. You must be signed in with this email to accept.
        </p>

        {wrongAccount && (
          <div className="ent-flash ent-flash-error" style={{ marginTop: 16 }} role="alert">
            You are signed in as a different email. Sign out and sign in with <strong>{invite?.email}</strong> to accept this invite.
          </div>
        )}

        {!currentUserEmail && (
          <p className="ent-page-subtitle" style={{ marginTop: 16 }}>
            <Link to="/login" state={{ returnUrl: `/enterprise-invite/${token}` }} className="ent-link" style={{ color: 'var(--ent-accent)', fontWeight: 600 }}>Log in</Link>
            {' or '}
            <Link to="/register" state={{ returnUrl: `/enterprise-invite/${token}` }} className="ent-link" style={{ color: 'var(--ent-accent)', fontWeight: 600 }}>register</Link>
            {' with '}{invite?.email}{' to continue.'}
          </p>
        )}

        {invite?.status === 'pending' && (
          <div className="ent-visibility-notice" role="note">
            <h2 className="ent-visibility-notice-title">Workspace visibility</h2>
            <p>
              By joining <strong>{invite?.enterpriseName}</strong>, contacts you save in your
              Velox account may be visible to your company&rsquo;s enterprise admins, including
              who you connected with and when.
            </p>
            <p>
              Your personal notes on contacts are <strong>never shared</strong> with enterprise
              admins.
            </p>
          </div>
        )}

        {readyToAccept && (
          <button
            type="button"
            className="ent-btn-primary"
            onClick={handleAccept}
            disabled={accepting}
            style={{ marginTop: 16 }}
          >
            {accepting ? 'Accepting…' : 'Accept invite'}
          </button>
        )}

        {currentUserEmail && !readyToAccept && !wrongAccount && invite?.status === 'pending' && (
          <p className="ent-page-subtitle" style={{ marginTop: 16 }}>Checking account…</p>
        )}
      </div>
    </div>
  );
}
